import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type { StaticDatabaseHandle } from "@-/dev.improve.haniverse.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import tbclEntries from "@-/com.npmjs.leonsilicon__tbcl";
import onetime from "@-/com.npmjs.onetime";
import { eq, sql } from "@-/com.npmjs.drizzle-orm";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import type { CharacterRow, CharactersGradeSection } from "#characters-grade-tbcl-page-entities";
import {
  TBCL_LEVELS,
  isTbclLevelKey,
  type TbclLevelKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.tbcl";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import {
  type MorphemeCorpusFilter,
  morphemeCorpusFilterFromCacheKey,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";

const getTbclCharactersByLevel = onetime((): Readonly<Record<TbclLevelKey, readonly string[]>> => {
  const byLevel = new Map<TbclLevelKey, string[]>();
  for (const level of TBCL_LEVELS) {
    byLevel.set(level, []);
  }
  for (const [, traditional, level] of tbclEntries) {
    if (!isTbclLevelKey(level)) {
      continue;
    }
    const chars = traditional.includes("/") ? traditional.split("/") : [traditional];
    const bucket = byLevel.get(level);
    if (bucket === undefined) {
      continue;
    }
    for (const c of chars) {
      if (c.length > 0) {
        bucket.push(c);
      }
    }
  }
  return {
    1: byLevel.get(1) ?? [],
    2: byLevel.get(2) ?? [],
    3: byLevel.get(3) ?? [],
    4: byLevel.get(4) ?? [],
    5: byLevel.get(5) ?? [],
    6: byLevel.get(6) ?? [],
    7: byLevel.get(7) ?? [],
  } satisfies Record<TbclLevelKey, readonly string[]>;
});

async function getCharacterRows(
  db: StaticDatabaseHandle,
  glyphs: readonly string[],
): Promise<readonly CharacterRow[]> {
  if (hasLength(glyphs, 0)) {
    return [];
  }
  const rows = await db
    .select({
      glyph: staticSchema.grapheme.glyph,
      rank: sql<number | null>`MIN(${staticSchema.graphemeSubtlex.rank})`.as("min_rank"),
      strokeCount: staticSchema.grapheme.strokeCount,
    })
    .from(staticSchema.grapheme)
    .leftJoin(
      staticSchema.graphemeSubtlex,
      eq(staticSchema.graphemeSubtlex.graphemeId, staticSchema.grapheme.id),
    )
    // A single bound JSON array works on local SQLite and D1 even when a band has hundreds of
    // characters. GROUP BY prevents the transport from safely chunking a large IN parameter list.
    .where(
      sql`${staticSchema.grapheme.glyph} IN (SELECT value FROM json_each(${JSON.stringify(glyphs)}))`,
    )
    .groupBy(staticSchema.grapheme.id);

  const byGlyph = new Map<string, CharacterRow>();
  for (const row of rows) {
    byGlyph.set(row.glyph, {
      glyph: row.glyph,
      rank: row.rank,
      strokeCount: row.strokeCount,
    });
  }
  return glyphs.map(
    (glyph): CharacterRow => byGlyph.get(glyph) ?? { glyph, rank: null, strokeCount: null },
  );
}

async function getTbclSections(
  db: StaticDatabaseHandle,
  corpusFilter: MorphemeCorpusFilter,
): Promise<readonly CharactersGradeSection[]> {
  const charactersByLevel = getTbclCharactersByLevel();
  const seen = new Set<string>();
  const sections: { glyphs: string[]; key: string; label: string }[] = [];
  for (const level of TBCL_LEVELS) {
    // Attribute each character to the first (lowest) level that introduces it, matching the
    // Levels-tab grouping so a character never appears under two band headers.
    const glyphs: string[] = [];
    for (const glyph of charactersByLevel[level]) {
      if (seen.has(glyph)) {
        continue;
      }
      seen.add(glyph);
      glyphs.push(glyph);
    }
    sections.push({
      // Attribution above runs on the full band list, so hiding a character never moves another
      // one into a different band; only then is the user's browsing corpus applied.
      glyphs: glyphs.filter((glyph) => corpusFilter.isGraphemeInCorpus(glyph)),
      key: `tbcl-${level}`,
      label: `TBCL ${level}`,
    });
  }
  // Sections differ only in grouping: fetch metadata for the deduplicated catalog once, then
  // project each ordered band. Previously every band waited for the previous network request.
  const rows = await getCharacterRows(
    db,
    sections.flatMap((section) => section.glyphs),
  );
  const byGlyph = new Map(rows.map((row) => [row.glyph, row]));
  return sections.map(({ glyphs, key, label }) => ({
    key,
    label,
    rows: glyphs.map(
      (glyph): CharacterRow => byGlyph.get(glyph) ?? { glyph, rank: null, strokeCount: null },
    ),
  }));
}

/**
 * Query options for the Characters → TBCL sections (every TBCL band's characters, sectioned by
 * band). Shared by `CharactersGradeTbclViewSuspense` (via `usePrefetchedSuspenseQuery`) and the
 * TBCL page navigator (which warms this before navigating). Derived entirely from the immutable
 * static DB + bundled TBCL entries, so the query key only varies with the user's browsing corpus
 * (its serializable `cacheKey`) and needs no change-version.
 */
function useGetCharactersGradeTbclPageQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { cacheKey: corpusFilterCacheKey } = useMorphemeCorpusFilter();
  return () =>
    ["characters-grade-tbcl-view", "sections", corpusFilterCacheKey, { staticDb }] as const;
}

const [useCharactersGradeTbclPageQueryOptions, charactersGradeTbclPageQueryFn] =
  createUseQueryOptions(
    useGetCharactersGradeTbclPageQueryKey,
    ({ queryKey: [_, _sections, corpusFilterCacheKey, { staticDb }] }) => {
      return getTbclSections(
        staticDb(import.meta.location),
        morphemeCorpusFilterFromCacheKey(corpusFilterCacheKey),
      );
    },
  );

export { useCharactersGradeTbclPageQueryOptions, charactersGradeTbclPageQueryFn };
